import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoJsonLd from '@/components/base/SeoJsonLd';
import {
  CategoryGrid,
  ComparisonTable,
  DifferentSection,
  MainPlatformSection,
  FeaturedGuidesSection,
  TrustSection,
  FinalCTA,
} from './components/HomeSections';
import { useEffect } from 'react';

function Hero() {
  return (
    <section className="relative w-full min-h-[520px] md:min-h-[650px] flex items-center justify-center overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20fashion%20product%20display%20with%20various%20clothing%20items%20shoes%20bags%20and%20accessories%20artfully%20arranged%20on%20a%20warm%20cream%20studio%20backdrop%20soft%20diffused%20daylight%20luxurious%20editorial%20aesthetic%20high%20detail%20professional%20catalog%20photography%20with%20subtle%20texture%20and%20refined%20neutral%20palette%20clean%20and%20sophisticated%20composition&width=1920&height=1080&seq=hero-main-v3&orientation=landscape"
        alt="OOPBuy Spreadsheet product showcase"
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/40"></div>
      <div className="relative z-10 w-full px-6 md:px-10 text-center">
        <div className="max-w-3xl mx-auto">
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-5 leading-tight">
            OOPBuy Spreadsheet Hub
          </h1>
          <p className="text-base md:text-lg text-background-50/90 mb-2 max-w-xl mx-auto leading-relaxed">
            The Most Complete Guide to OOPBuy Spreadsheet Systems
          </p>
          <p className="text-sm md:text-base text-background-50/75 max-w-lg mx-auto mb-8 leading-relaxed">
            Discover how OOPBuy Spreadsheet tools help you organize, compare, and optimize buying decisions
            with powerful structured data tracking and smart spreadsheet systems. Built for users who want
            faster decisions, clearer comparisons, and smarter purchasing workflows.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/guide/oopbuy-spreadsheet-complete-guide"
              className="px-7 py-3.5 bg-primary-500 text-background-50 dark:text-foreground-950 text-sm md:text-base font-semibold rounded-md hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
            >
              Explore OOPBuy Spreadsheet Guides
            </Link>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-7 py-3.5 bg-background-50 text-foreground-900 text-sm md:text-base font-semibold rounded-md hover:bg-background-100 transition-all cursor-pointer whitespace-nowrap"
            >
              Access Main Platform
            </a>
          </div>
          <p className="text-xs text-background-50/60 mt-4">
            Trusted informational hub for spreadsheet-based buying systems, comparison tools, and automation guides.
          </p>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="py-14 md:py-20 px-6 md:px-10 bg-background-50">
      <div className="max-w-4xl mx-auto">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
          What is OOPBuy Spreadsheet?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
          <div className="md:col-span-3">
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              OOPBuy Spreadsheet is a structured data system designed to help users organize product information,
              compare options, and track purchasing decisions more efficiently than traditional spreadsheets.
              This website is a <strong className="text-foreground-900">central knowledge hub</strong> built to
              explain how OOPBuy Spreadsheet systems work, compare different spreadsheet tools and methods,
              help users make faster data-driven decisions, and redirect advanced users to the main OOPBuy platform.
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              Whether you are a casual shopper looking for better organization or a professional buyer managing
              complex purchasing workflows, OOPBuy Spreadsheet provides the structured framework you need to
              make informed choices with confidence and precision.
            </p>
          </div>
          <div className="md:col-span-2">
            <div className="bg-background-100 border border-background-200/70 rounded-lg p-5">
              <h3 className="font-heading text-base font-semibold text-foreground-900 mb-3">This Hub Helps You:</h3>
              <ul className="space-y-2.5">
                {[
                  'Understand spreadsheet buying systems',
                  'Compare tools and methodologies',
                  'Learn automation techniques',
                  'Make faster data-driven decisions',
                  'Access the main OOPBuy platform',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-foreground-600">
                    <div className="w-4 h-4 flex-shrink-0 flex items-center justify-center mt-0.5">
                      <i className="ri-check-line text-accent-500 text-sm"></i>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://oopbuyspreadsheets.lifestyle';

  useEffect(() => {
    document.title = 'OOPBuy Spreadsheet Hub – The Most Complete Guide to OOPBuy Spreadsheet Systems';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Discover how OOPBuy Spreadsheet tools help you organize, compare, and optimize buying decisions with structured data tracking and smart spreadsheet systems. Learn about spreadsheet buying systems, comparison tools, and automation guides.'
      );
    }
  }, []);

  const websiteJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'OOPBuy Spreadsheet Hub',
    url: siteUrl,
    description:
      'The most complete guide to OOPBuy Spreadsheet systems — learn how structured data tracking, comparison tools, and automation strategies transform buying decisions.',
    inLanguage: 'en',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteUrl}/guide/oopbuy-spreadsheet-{search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  const webpageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'OOPBuy Spreadsheet Hub – The Most Complete Guide to OOPBuy Spreadsheet Systems',
    description:
      'Discover how OOPBuy Spreadsheet tools help you organize, compare, and optimize buying decisions with structured data tracking and smart spreadsheet systems.',
    url: siteUrl,
    isPartOf: {
      '@type': 'WebSite',
      name: 'OOPBuy Spreadsheet Hub',
      url: siteUrl,
    },
    about: {
      '@type': 'Thing',
      name: 'OOPBuy Spreadsheet',
      description:
        'A structured data system designed to help users organize product information, compare options, and track purchasing decisions more efficiently than traditional spreadsheets.',
    },
  };

  return (
    <main>
      <SeoJsonLd data={websiteJsonLd} />
      <SeoJsonLd data={webpageJsonLd} />
      <Navbar transparent={true} />
      <Hero />
      <WhatIsSection />
      <CategoryGrid />
      <ComparisonTable />
      <DifferentSection />
      <MainPlatformSection />
      <FeaturedGuidesSection />
      <TrustSection />
      <FinalCTA />
      <Footer />
    </main>
  );
}